import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Search as SearchIcon, 
  Visibility as ViewIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Wc as GenderIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  HowToReg as CheckInIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { patientAPI, visitAPI, medicalHistoryAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import StatCard from '../components/StatCard';

const InfoItem = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="body2" color="#7B7A92" sx={{ mb: 0.5, fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="body1" color="#56546E">
      {value || 'N/A'}
    </Typography>
  </Box>
);

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return isNaN(d) ? '' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Patients = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [checkInStartTime, setCheckInStartTime] = useState(null);
  const [openCheckInDialog, setOpenCheckInDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('select'); // 'select' | 'new' | 'existing' | 'edit'
  const [checkInPatient, setCheckInPatient] = useState(null);
  const [checkInPatients, setCheckInPatients] = useState([]);
  const [newPatientLoading, setNewPatientLoading] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [editingPatient, setEditingPatient] = useState(null);
  const emptyPatientForm = {
    name: '',
    age: '',
    gender: 'Male',
    dateOfBirth: '',
    sonWifeDaughterOf: '',
    maritalStatus: 'Married',
    profession: '',
    contact: { phone: '', email: '' },
    address: { street: '', city: '', state: '', pincode: '' },
    referralSource: '',
    medicalHistory: { conditions: [] },
    chiefComplaint: '',
    referTo: 'optometrist',
    vitalSigns: { bloodPressure: '', temperature: '', pulse: '' },
    fees: {
      consultationFee: '',
      optometryFee: '',
      surgeryFee: '',
      investigationFee: '',
      otherFees: '',
      otherFeesDescription: '',
    },
  };
  const [newPatientForm, setNewPatientForm] = useState(emptyPatientForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({
    total: 0,
    male: 0,
    female: 0,
    newThisMonth: 0
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCheckIn = ['reception', 'admin'].includes(user?.role);

  useEffect(() => {
    fetchPatients();
  }, [search]);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await patientAPI.getPatients({ search, limit: 100 });
      const patientsData = response.data.data;
      setPatients(patientsData);
      
      // Calculate stats
      const total = patientsData.length;
      const male = patientsData.filter(p => p.gender === 'Male').length;
      const female = patientsData.filter(p => p.gender === 'Female').length;
      const thisMonth = new Date();
      thisMonth.setDate(1);
      thisMonth.setHours(0, 0, 0, 0);
      const newThisMonth = patientsData.filter(p => new Date(p.createdAt) >= thisMonth).length;
      
      setStats({ total, male, female, newThisMonth });
    } catch (error) {
      showSnackbar('Failed to fetch patients', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleViewPatient = async (id) => {
    try {
      const response = await patientAPI.getPatient(id);
      setSelectedPatient(response.data.data);
      setOpenViewDialog(true);
    } catch (error) {
      console.error('Error fetching patient details:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch patient details',
        severity: 'error'
      });
    }
  };

  const setNewPatientField = (field, value) => {
    setNewPatientForm((prev) => ({ ...prev, [field]: value }));
  };

  const loadConditions = () => {
    medicalHistoryAPI.getConditions()
      .then((res) => setConditionOptions(res.data.data))
      .catch(() => setConditionOptions([]));
  };

  const handleOpenEdit = (patient) => {
    setEditingPatient(patient);
    setNewPatientForm({
      ...emptyPatientForm,
      name: patient.name || '',
      age: patient.age || '',
      gender: patient.gender || 'Male',
      dateOfBirth: patient.dateOfBirth ? String(patient.dateOfBirth).slice(0, 10) : '',
      sonWifeDaughterOf: patient.sonWifeDaughterOf || '',
      maritalStatus: patient.maritalStatus || 'Married',
      profession: patient.profession || '',
      contact: { phone: patient.contact?.phone || '', email: patient.contact?.email || '' },
      address: { street: '', city: '', state: '', pincode: '', ...(patient.address || {}) },
      referralSource: patient.referralSource || '',
      medicalHistory: { conditions: patient.medicalHistory?.conditions || [] },
    });
    setDialogMode('edit');
    setOpenViewDialog(false);
    setOpenCheckInDialog(true);
    loadConditions();
  };

  const openPatientCheckIn = () => {
    setEditingPatient(null);
    setCheckInPatient(null);
    setDialogMode('select');
    setNewPatientForm(emptyPatientForm);
    setCheckInStartTime(new Date());
    setOpenCheckInDialog(true);
    loadConditions();
    patientAPI.getPatients({ limit: 500 })
      .then((res) => setCheckInPatients(res.data.data))
      .catch(() => setCheckInPatients([]));
  };

  const handleSelectExisting = (patient) => {
    if (!patient) return;
    setCheckInPatient(patient);
    setDialogMode('existing');
  };

  const closeCheckInDialog = () => {
    setOpenCheckInDialog(false);
    setEditingPatient(null);
    setCheckInPatient(null);
    setDialogMode('select');
    setNewPatientForm(emptyPatientForm);
    setCheckInStartTime(null);
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const birth = new Date(dob);
    if (isNaN(birth)) return '';
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
    return age >= 0 ? age : '';
  };

  const handleDobChange = (value) => {
    setNewPatientForm((prev) => ({
      ...prev,
      dateOfBirth: value,
      age: calculateAge(value),
    }));
  };

  const handlePincodeChange = async (value) => {
    const pincode = value.replace(/\D/g, '').slice(0, 6);
    setNewPatientForm((prev) => ({
      ...prev,
      address: { ...prev.address, pincode },
    }));

    if (pincode.length === 6) {
      try {
        setPincodeLoading(true);
        const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
        const data = await res.json();
        const postOffice = data?.[0]?.PostOffice?.[0];
        if (postOffice) {
          setNewPatientForm((prev) => ({
            ...prev,
            address: {
              ...prev.address,
              pincode,
              city: postOffice.District || prev.address.city,
              state: postOffice.State || prev.address.state,
            },
          }));
        } else {
          showSnackbar('Invalid pincode — city/state not found', 'warning');
        }
      } catch (e) {
        // Lookup is best-effort; user can type city/state manually
      } finally {
        setPincodeLoading(false);
      }
    }
  };

  const handleNewPatientSubmit = async () => {
    try {
      setNewPatientLoading(true);

      const receptionDuration = checkInStartTime
        ? Math.floor((new Date() - checkInStartTime) / 1000)
        : 0;

      const feesPayload = {
        consultationFee: parseFloat(newPatientForm.fees.consultationFee) || 0,
        optometryFee: parseFloat(newPatientForm.fees.optometryFee) || 0,
        surgeryFee: parseFloat(newPatientForm.fees.surgeryFee) || 0,
        investigationFee: parseFloat(newPatientForm.fees.investigationFee) || 0,
        otherFees: parseFloat(newPatientForm.fees.otherFees) || 0,
        otherFeesDescription: newPatientForm.fees.otherFeesDescription,
      };

      // Existing patient check-in: create visit only
      if (dialogMode === 'existing' && checkInPatient) {
        const visitResponse = await visitAPI.createVisit({
          patientId: checkInPatient._id,
          chiefComplaint: newPatientForm.chiefComplaint,
          vitalSigns: newPatientForm.vitalSigns,
          referTo: newPatientForm.referTo,
          receptionDuration,
          fees: feesPayload,
        });
        const token = visitResponse.data?.data?.tokenNumber;
        showSnackbar(
          token
            ? `${checkInPatient.name} checked in — Token #${token}`
            : `${checkInPatient.name} checked in successfully`
        );
        closeCheckInDialog();
        fetchPatients();
        return;
      }

      if (editingPatient) {
        await patientAPI.updatePatient(editingPatient._id, {
          name: newPatientForm.name,
          age: newPatientForm.age,
          gender: newPatientForm.gender,
          dateOfBirth: newPatientForm.dateOfBirth || undefined,
          sonWifeDaughterOf: newPatientForm.sonWifeDaughterOf,
          maritalStatus: newPatientForm.maritalStatus,
          profession: newPatientForm.profession,
          contact: newPatientForm.contact,
          address: newPatientForm.address,
          referralSource: newPatientForm.referralSource,
          medicalHistory: newPatientForm.medicalHistory,
        });
        showSnackbar(`${newPatientForm.name} updated successfully`);
        closeCheckInDialog();
        fetchPatients();
        return;
      }

      const patientResponse = await patientAPI.createPatient({
        name: newPatientForm.name,
        age: newPatientForm.age,
        gender: newPatientForm.gender,
        dateOfBirth: newPatientForm.dateOfBirth || undefined,
        sonWifeDaughterOf: newPatientForm.sonWifeDaughterOf,
        maritalStatus: newPatientForm.maritalStatus,
        profession: newPatientForm.profession,
        contact: newPatientForm.contact,
        address: newPatientForm.address,
        referralSource: newPatientForm.referralSource,
        medicalHistory: newPatientForm.medicalHistory,
      });
      const created = patientResponse.data.data;

      const visitResponse = await visitAPI.createVisit({
        patientId: created._id,
        chiefComplaint: newPatientForm.chiefComplaint,
        vitalSigns: newPatientForm.vitalSigns,
        referTo: newPatientForm.referTo,
        receptionDuration,
        fees: feesPayload,
      });

      const token = visitResponse.data?.data?.tokenNumber;
      showSnackbar(
        token
          ? `${created.name} registered & checked in — Token #${token}`
          : `${created.name} registered & checked in`
      );
      closeCheckInDialog();
      fetchPatients();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to register patient', 'error');
    } finally {
      setNewPatientLoading(false);
    }
  };

  const columns = [
    { field: 'patientId', headerName: 'Patient ID', width: 130 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'age', headerName: 'Age', width: 80 },
    { field: 'gender', headerName: 'Gender', width: 100 },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
      valueGetter: (params) => params.row.contact?.phone,
    },
    {
      field: 'city',
      headerName: 'City',
      width: 150,
      valueGetter: (params) => params.row.address?.city || 'N/A',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton color="primary" onClick={() => handleViewPatient(params.row._id)}>
              <ViewIcon />
            </IconButton>
          </Tooltip>
          {canCheckIn && (
            <Tooltip title="Edit Patient">
              <IconButton color="secondary" onClick={() => handleOpenEdit(params.row)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h3" fontWeight="700" color="#3D3B54" sx={{ mb: 1 }}>
              Patients
            </Typography>
            <Typography variant="body1" color="#7B7A92">
              Manage and view all patient records
            </Typography>
          </Box>
        </Box>

        {/* Stats Cards Grid */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatCard label="Total Patients" value={stats.total} icon={<PeopleIcon fontSize="small" />} color="primary" />
          <StatCard label="Male Patients" value={stats.male} icon={<GenderIcon fontSize="small" />} color="info" />
          <StatCard label="Female Patients" value={stats.female} icon={<GenderIcon fontSize="small" />} color="secondary" />
          <StatCard label="New This Month" value={stats.newThisMonth} icon={<TrendingUpIcon fontSize="small" />} color="success" />
        </Box>
      </Box>

      {/* Search Bar */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          backgroundColor: '#ffffff',
          boxShadow: 'none',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Search by name, patient ID, or phone number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.875rem',
              },
            }}
          />
          {canCheckIn && (
            <Button
              variant="contained"
              startIcon={<CheckInIcon />}
              onClick={openPatientCheckIn}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Patient Check In
            </Button>
          )}
        </Box>
      </Paper>

      {/* Data Grid */}
      <Paper
        variant="outlined"
        sx={{
          height: 600,
          width: '100%',
          borderRadius: 2,
          backgroundColor: '#ffffff',
          overflow: 'hidden',
          boxShadow: 'none'
        }}
      >
        <DataGrid
          rows={patients}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          loading={loading}
          getRowId={(row) => row._id}
          disableSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#FAFAFD',
              borderBottom: '2px solid #E9E9F2',
            },
            '& .MuiDataGrid-columnHeader': {
              color: '#7B7A92',
              fontWeight: 600,
              fontSize: '0.8125rem',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #F4F4F9',
              color: '#56546E',
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-cell:hover': {
              color: '#626AC4',
              backgroundColor: '#FAFAFD',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#FAFAFD',
            },
            '& .MuiDataGrid-row': {
              '&:last-child .MuiDataGrid-cell': {
                borderBottom: 'none',
              },
            },
          }}
        />
      </Paper>

      {/* View Patient Dialog - Enhanced */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Patient Details
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedPatient && (
            <Box>
              {/* Patient Header */}
              <Card 
                elevation={0}
                sx={{ 
                  mb: 3, 
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid #E9E9F2',
                  backgroundColor: '#FAFAFD',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{
                    width: 64,
                    height: 64,
                    mr: 2.5,
                    bgcolor: 'primary.main',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                  }}>
                    {selectedPatient.name.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight="700" sx={{ mb: 1 }}>
                      {selectedPatient.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip label={selectedPatient.patientId} size="small" color="primary" variant="outlined" />
                      <Chip label={`${selectedPatient.age} years`} size="small" variant="outlined" />
                      <Chip label={selectedPatient.gender} size="small" variant="outlined" />
                    </Box>
                  </Box>
                </Box>
              </Card>

              <Grid container spacing={3}>
                {/* Personal Details */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid #E9E9F2',
                      backgroundColor: '#ffffff',
                      height: '100%'
                    }}
                  >
                    <Typography variant="h6" fontWeight="600" color="#3D3B54" sx={{ mb: 2 }}>
                      Personal Details
                    </Typography>
                    <InfoItem label="Date of Birth" value={formatDate(selectedPatient.dateOfBirth)} />
                    <InfoItem label="S/o W/o D/o" value={selectedPatient.sonWifeDaughterOf} />
                    <InfoItem label="Marital Status" value={selectedPatient.maritalStatus} />
                    <InfoItem label="Profession" value={selectedPatient.profession} />
                  </Card>
                </Grid>

                {/* Contact Info */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid #E9E9F2',
                      backgroundColor: '#ffffff',
                      height: '100%'
                    }}
                  >
                    <Typography variant="h6" fontWeight="600" color="#3D3B54" sx={{ mb: 2 }}>
                      Contact Information
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PhoneIcon sx={{ mr: 2, fontSize: 20, color: '#626AC4' }} />
                      <Typography variant="body1" color="#56546E">{selectedPatient.contact?.phone}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <EmailIcon sx={{ mr: 2, fontSize: 20, color: '#626AC4' }} />
                      <Typography variant="body1" color="#56546E">
                        {selectedPatient.contact?.email || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <LocationIcon sx={{ mr: 2, fontSize: 20, color: '#626AC4', mt: 0.5 }} />
                      <Typography variant="body1" color="#56546E">
                        {[selectedPatient.address?.street, selectedPatient.address?.city, selectedPatient.address?.state].filter(Boolean).join(', ') || 'N/A'}
                        {selectedPatient.address?.pincode ? ` - ${selectedPatient.address.pincode}` : ''}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>

                {/* Medical History */}
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid #E9E9F2',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    <Typography variant="h6" fontWeight="600" color="#3D3B54" sx={{ mb: 2 }}>
                      Medical History
                    </Typography>
                    {selectedPatient.medicalHistory?.conditions?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="#7B7A92" sx={{ mb: 1, fontWeight: 600 }}>
                          Conditions
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {selectedPatient.medicalHistory.conditions.map((c) => (
                            <Chip key={c} label={c} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <InfoItem label="Allergies" value={selectedPatient.medicalHistory?.allergies?.join(', ')} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InfoItem label="Current Medications" value={selectedPatient.medicalHistory?.currentMedications?.join(', ')} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <InfoItem label="Family History" value={selectedPatient.medicalHistory?.familyHistory} />
                      </Grid>
                    </Grid>
                    {!selectedPatient.medicalHistory?.conditions?.length &&
                     !selectedPatient.medicalHistory?.allergies?.length &&
                     !selectedPatient.medicalHistory?.currentMedications?.length &&
                     !selectedPatient.medicalHistory?.familyHistory && (
                      <Typography variant="body2" color="text.secondary">No medical history recorded</Typography>
                    )}
                  </Card>
                </Grid>

                {/* Insurance & Referral */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid #E9E9F2',
                      backgroundColor: '#ffffff',
                      height: '100%'
                    }}
                  >
                    <Typography variant="h6" fontWeight="600" color="#3D3B54" sx={{ mb: 2 }}>
                      Insurance
                    </Typography>
                    <InfoItem label="Provider" value={selectedPatient.insurance?.provider} />
                    <InfoItem label="Policy Number" value={selectedPatient.insurance?.policyNumber} />
                    <InfoItem label="Valid Until" value={formatDate(selectedPatient.insurance?.validUntil)} />
                  </Card>
                </Grid>

                {/* Additional Info */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid #E9E9F2',
                      backgroundColor: '#ffffff',
                      height: '100%'
                    }}
                  >
                    <Typography variant="h6" fontWeight="600" color="#3D3B54" sx={{ mb: 2 }}>
                      Additional Information
                    </Typography>
                    <InfoItem label="Referral Source" value={selectedPatient.referralSource} />
                    <InfoItem label="Primary Complaint" value={selectedPatient.primaryComplaint} />
                    <InfoItem label="Registered On" value={formatDate(selectedPatient.createdAt)} />
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          {canCheckIn && selectedPatient && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<EditIcon />}
              onClick={() => handleOpenEdit(selectedPatient)}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
              }}
            >
              Edit
            </Button>
          )}
          <Button 
            onClick={() => setOpenViewDialog(false)} 
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Patient Check-In / Registration Dialog */}
      <Dialog
        open={openCheckInDialog}
        onClose={closeCheckInDialog}
        maxWidth={dialogMode === 'select' ? 'sm' : 'md'}
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckInIcon sx={{ color: 'primary.main' }} />
          {dialogMode === 'edit'
            ? `Edit Patient — ${editingPatient?.name}`
            : dialogMode === 'existing'
              ? `Check-In: ${checkInPatient?.name}`
              : 'Patient Check-In'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Step 1: search existing or register new */}
            {dialogMode === 'select' && (
              <>
                <Grid item xs={12}>
                  <Autocomplete
                    options={checkInPatients}
                    getOptionLabel={(o) => `${o.name} - ${o.patientId} (${o.contact?.phone || ''})`}
                    onChange={(e, v) => handleSelectExisting(v)}
                    renderInput={(params) => (
                      <TextField {...params} label="Search Existing Patient" placeholder="Search by name, ID, or phone" />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button fullWidth variant="outlined" onClick={() => setDialogMode('new')}>
                    + Register New Patient
                  </Button>
                </Grid>
              </>
            )}

            {/* Existing patient banner */}
            {dialogMode === 'existing' && checkInPatient && (
              <Grid item xs={12}>
                <Alert severity="success" icon={<CheckInIcon />}>
                  Checking in <strong>{checkInPatient.name}</strong> — {checkInPatient.patientId} • {checkInPatient.age} yrs • {checkInPatient.gender}
                </Alert>
              </Grid>
            )}

            {/* Patient registration fields (new / edit only) */}
            {(dialogMode === 'new' || dialogMode === 'edit') && (
            <>
            {/* Name / S/o W/o D/o */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Name"
                value={newPatientForm.name}
                onChange={(e) => setNewPatientField('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="S/o W/o D/o"
                placeholder="Son of / Wife of / Daughter of"
                value={newPatientForm.sonWifeDaughterOf}
                onChange={(e) => setNewPatientField('sonWifeDaughterOf', e.target.value)}
              />
            </Grid>

            {/* DOB / Age / Sex */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Date of Birth" type="date"
                value={newPatientForm.dateOfBirth}
                onChange={(e) => handleDobChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().slice(0, 10) }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth required label="Age" type="number"
                value={newPatientForm.age}
                onChange={(e) => setNewPatientField('age', e.target.value)}
                inputProps={{ min: 0, max: 130 }}
                helperText={newPatientForm.dateOfBirth ? 'Auto-calculated from DOB' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Sex</InputLabel>
                <Select
                  value={newPatientForm.gender} label="Sex"
                  onChange={(e) => setNewPatientField('gender', e.target.value)}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Marital Status / Profession */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Marital Status</InputLabel>
                <Select
                  value={newPatientForm.maritalStatus} label="Marital Status"
                  onChange={(e) => setNewPatientField('maritalStatus', e.target.value)}
                >
                  <MenuItem value="Married">Married</MenuItem>
                  <MenuItem value="Unmarried">Unmarried</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Profession"
                value={newPatientForm.profession}
                onChange={(e) => setNewPatientField('profession', e.target.value)}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <TextField
                fullWidth label="Address" multiline rows={2}
                value={newPatientForm.address.street}
                onChange={(e) => setNewPatientField('address', { ...newPatientForm.address, street: e.target.value })}
              />
            </Grid>

            {/* Pin Code / City / State */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Pin Code"
                value={newPatientForm.address.pincode}
                onChange={(e) => handlePincodeChange(e.target.value)}
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                InputProps={{
                  endAdornment: pincodeLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={18} />
                    </InputAdornment>
                  ) : null,
                }}
                helperText="Auto-fills city & state"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="City"
                value={newPatientForm.address.city}
                onChange={(e) => setNewPatientField('address', { ...newPatientForm.address, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="State"
                value={newPatientForm.address.state}
                onChange={(e) => setNewPatientField('address', { ...newPatientForm.address, state: e.target.value })}
              />
            </Grid>

            {/* Mobile / Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Mobile No."
                value={newPatientForm.contact.phone}
                onChange={(e) => setNewPatientField('contact', { ...newPatientForm.contact, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Email" type="email"
                value={newPatientForm.contact.email}
                onChange={(e) => setNewPatientField('contact', { ...newPatientForm.contact, email: e.target.value })}
              />
            </Grid>

            {/* Referral Source */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Where did you get the information about us?</InputLabel>
                <Select
                  value={newPatientForm.referralSource}
                  label="Where did you get the information about us?"
                  onChange={(e) => setNewPatientField('referralSource', e.target.value)}
                >
                  <MenuItem value="">Select source</MenuItem>
                  <MenuItem value="Just dial">Just dial</MenuItem>
                  <MenuItem value="Newspaper">Newspaper</MenuItem>
                  <MenuItem value="Pamphlet">Pamphlet</MenuItem>
                  <MenuItem value="Board">Board</MenuItem>
                  <MenuItem value="Friend or Relation">Friend or Relation</MenuItem>
                  <MenuItem value="Doctor Referral">Doctor Referral</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Medical History */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>
                Medical History
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={conditionOptions}
                getOptionLabel={(option) => option.name}
                value={conditionOptions.filter((c) =>
                  (newPatientForm.medicalHistory.conditions || []).includes(c.name)
                )}
                onChange={(e, newValue) =>
                  setNewPatientField('medicalHistory', {
                    ...newPatientForm.medicalHistory,
                    conditions: newValue.map((c) => c.name),
                  })
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option._id}
                      label={option.name}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search & select medical conditions"
                    placeholder="Type to search (e.g. Cardiac, Asthma)"
                  />
                )}
              />
            </Grid>
            </>
            )}

            {/* Visit Details */}
            {dialogMode !== 'select' && (
            <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                Visit Details
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Chief Complaint" multiline rows={2}
                placeholder="Why is the patient visiting today?"
                value={newPatientForm.chiefComplaint}
                onChange={(e) => setNewPatientField('chiefComplaint', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Refer To</InputLabel>
                <Select
                  value={newPatientForm.referTo} label="Refer To"
                  onChange={(e) => setNewPatientField('referTo', e.target.value)}
                >
                  <MenuItem value="optometrist">Optometrist</MenuItem>
                  <MenuItem value="doctor">Doctor</MenuItem>
                  <MenuItem value="opd">OPD (Billing/Dispensing)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Alert severity="info" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                Patient will be sent to: <strong style={{ marginLeft: 4 }}>
                  {newPatientForm.referTo === 'optometrist' ? 'Optometrist' :
                   newPatientForm.referTo === 'doctor' ? 'Doctor' : 'OPD'}
                </strong>
              </Alert>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Blood Pressure" placeholder="120/80"
                value={newPatientForm.vitalSigns.bloodPressure}
                onChange={(e) => setNewPatientField('vitalSigns', { ...newPatientForm.vitalSigns, bloodPressure: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Temperature" placeholder="98.6°F"
                value={newPatientForm.vitalSigns.temperature}
                onChange={(e) => setNewPatientField('vitalSigns', { ...newPatientForm.vitalSigns, temperature: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Pulse" placeholder="72 bpm"
                value={newPatientForm.vitalSigns.pulse}
                onChange={(e) => setNewPatientField('vitalSigns', { ...newPatientForm.vitalSigns, pulse: e.target.value })}
              />
            </Grid>

            {/* Fee Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Fee Details
              </Typography>
            </Grid>
            {[
              { key: 'consultationFee', label: 'Consultation Fee' },
              { key: 'optometryFee', label: 'Optometry Fee' },
              { key: 'surgeryFee', label: 'Surgery Fee' },
              { key: 'investigationFee', label: 'Investigation/Tests Fee' },
              { key: 'otherFees', label: 'Other Fees' },
            ].map(({ key, label }) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth label={label} type="number" placeholder="0"
                  value={newPatientForm.fees[key]}
                  onChange={(e) => setNewPatientField('fees', { ...newPatientForm.fees, [key]: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Other Fees Description"
                placeholder="e.g., Dilation drops, Special tests"
                value={newPatientForm.fees.otherFeesDescription}
                onChange={(e) => setNewPatientField('fees', { ...newPatientForm.fees, otherFeesDescription: e.target.value })}
              />
            </Grid>
            </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {(dialogMode === 'new' || dialogMode === 'existing') && (
            <Button
              onClick={() => { setDialogMode('select'); setCheckInPatient(null); setNewPatientForm(emptyPatientForm); }}
              variant="outlined"
            >
              Back
            </Button>
          )}
          <Button onClick={closeCheckInDialog} variant="outlined">
            Cancel
          </Button>
          {dialogMode !== 'select' && (
            <Button
              onClick={handleNewPatientSubmit}
              variant="contained"
              disabled={
                newPatientLoading ||
                (dialogMode !== 'existing' &&
                  (!newPatientForm.name.trim() || !newPatientForm.age || !newPatientForm.contact.phone.trim()))
              }
            >
              {newPatientLoading
                ? 'Saving…'
                : dialogMode === 'edit'
                  ? 'Update Patient'
                  : dialogMode === 'new'
                    ? 'Register & Check-In'
                    : 'Check-In'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Patients;
